import { openChapterReader, buildProseFromParagraphs, closeChapterReaderInstant, openNovelPrologue } from './ChapterReader.js';
import { showEmailPopover } from './EmailForm.js';
import { stories } from '../data/stories.js';

/*
  StoryPicker — 3D ring carousel for selecting a story within a collection.

  Rule 74 compliant: the overlay is created ONCE and stays in the DOM
  permanently. Opening/closing toggles visibility via class + opacity,
  never appends/removes the fixed-position element itself.

  Rule 75 compliant: scroll lock uses position:fixed + top:-scrollY on
  body (not overflow:hidden), restoring scroll position on close.

  Usage: openStoryPicker(stories) from data/stories.js.
  status: 'released'    -> center-click opens Reader.js via openReader()
  status: anything else -> card stays dimmed even at center, center-click
                            reveals createEmailForm() inline instead
*/

let cachedOverlay = null;
let cachedRenderFn = null;
let lockedScrollY = 0;

function lockBodyScroll() {
  lockedScrollY = window.scrollY || window.pageYOffset || 0;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${lockedScrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';
}

function unlockBodyScroll() {
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';
  window.scrollTo(0, lockedScrollY);
}

function buildPicker() {
  const overlay = document.createElement('div');
  overlay.dataset.role = 'story-picker-overlay';
  overlay.className = 'fixed inset-0 z-50 flex flex-col items-center justify-center px-5 pt-3 pb-6';
  overlay.style.background = 'rgba(5,5,7,0.72)';
  overlay.style.backdropFilter = 'blur(24px) saturate(0.8)';
  overlay.style.webkitBackdropFilter = 'blur(24px) saturate(0.8)';
  // hidden by default — Rule 74: stays in DOM, toggled via opacity/visibility only
  overlay.style.opacity = '0';
  overlay.style.visibility = 'hidden';
  overlay.style.pointerEvents = 'none';
  overlay.style.transition = 'opacity 0.4s ease, transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)';
  overlay.style.transform = 'scale(0.97)';

  const closeBtn = document.createElement('button');
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.className = 'absolute top-6 right-6 text-neutral-300 hover:text-white text-2xl leading-none z-20';
  closeBtn.textContent = '\u2715';
  closeBtn.addEventListener('click', () => hidePicker());

  const header = document.createElement('div');
  header.dataset.role = 'picker-header';
  header.className = 'text-center mb-4 max-w-md shrink-0';
  header.style.minHeight = '130px';
  header.innerHTML = `
    <div class="text-[10px] tracking-[0.35em] uppercase text-neutral-500 mb-1 font-light">MYTH'S SHORTS</div>
    <div data-role="picker-collection-label" class="text-[10px] tracking-[0.35em] uppercase text-neutral-500 mb-5 font-light"></div>
    <h2 data-role="picker-title" class="font-story text-3xl md:text-4xl uppercase tracking-widest mb-2 transition-opacity duration-500 ease-in-out"></h2>
    <p data-role="picker-hook" class="font-story italic text-sm text-neutral-400 min-h-[1.25rem] transition-opacity duration-500 ease-in-out"></p>
  `;
  const titleEl = header.querySelector('[data-role="picker-title"]');
  const hookEl = header.querySelector('[data-role="picker-hook"]');

  const ringStage = document.createElement('div');
  ringStage.dataset.role = 'picker-ring-stage';
  ringStage.className = 'relative w-full flex items-center justify-center shrink-0';
  ringStage.style.maxWidth = '1100px';
  ringStage.style.height = '340px';
  ringStage.style.perspective = '1200px';
  ringStage.style.perspectiveOrigin = '50% 50%';

  const ringTrack = document.createElement('div');
  ringTrack.className = 'relative w-full h-full';
  ringTrack.style.transformStyle = 'preserve-3d';

  const prevBtn = document.createElement('button');
  prevBtn.textContent = '\u2039';
  prevBtn.setAttribute('aria-label', 'Previous story');
  prevBtn.className = 'absolute top-1/2 -translate-y-1/2 text-white/35 hover:text-white/90 text-2xl px-5 z-20 transition-colors';
  prevBtn.style.left = '0';

  const nextBtn = document.createElement('button');
  nextBtn.textContent = '\u203a';
  nextBtn.setAttribute('aria-label', 'Next story');
  nextBtn.className = 'absolute top-1/2 -translate-y-1/2 text-white/35 hover:text-white/90 text-2xl px-5 z-20 transition-colors';
  nextBtn.style.right = '0';

  // counter now lives in normal flow BELOW the ring stage, not absolutely
  // positioned inside it — this is what was colliding with ENTER in landscape
  const counterEl = document.createElement('div');
  counterEl.className = 'text-[10px] tracking-[0.28em] text-white/30 mt-2 shrink-0';

  const enterBtn = document.createElement('button');
  enterBtn.textContent = 'READ';
  enterBtn.className = 'mt-5 border border-neutral-500 text-neutral-200 text-xs tracking-[0.3em] uppercase px-10 py-3 opacity-0 pointer-events-none transition-all duration-500 shrink-0';

  const formSlot = document.createElement('div');
  formSlot.dataset.role = 'picker-form-slot';
  formSlot.className = 'mt-5 mb-4 w-full flex justify-center shrink-0';

  ringStage.append(prevBtn, ringTrack, nextBtn);

  const shortViewportStyle = document.createElement('style');
  shortViewportStyle.textContent = `
    @media (orientation: landscape) {
      [data-role="picker-ring-stage"] { height: 230px !important; }
      .picker-card-el { width: 150px !important; height: 210px !important; }
      [data-role="picker-header"] { min-height: 90px !important; margin-bottom: 8px !important; }
    }
  `;
  overlay.append(shortViewportStyle, closeBtn, header, ringStage, counterEl, enterBtn, formSlot);
  document.getElementById('app-root').appendChild(overlay);

  function showPicker(stories) {
    lockBodyScroll();
    overlay.style.visibility = 'visible';
    overlay.style.pointerEvents = 'auto';
    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
      overlay.style.transform = 'scale(1)';
    });
    renderRing(stories, { titleEl, hookEl, ringTrack, counterEl, enterBtn, formSlot });
  }

  function hidePicker() {
    overlay.style.opacity = '0';
    overlay.style.transform = 'scale(0.97)';
    overlay.style.pointerEvents = 'none';
    setTimeout(() => {
      overlay.style.visibility = 'hidden';
    }, 400);
    unlockBodyScroll();
  }

  window.addEventListener('keydown', (e) => {
    if (overlay.style.visibility !== 'visible') return;
    if (e.key === 'Escape') hidePicker();
  });

  return { overlay, showPicker, hidePicker };
}

function renderRing(stories, refs) {
  const { titleEl, hookEl, ringTrack, counterEl, enterBtn, formSlot } = refs;
  const N = stories.length;
  const collectionLabelEl = document.querySelector('[data-role="picker-collection-label"]');
  if (collectionLabelEl && stories[0]) {
    collectionLabelEl.textContent = `${stories[0].collectionTitle || ''} \u2014 SELECT A STORY`;
  }
  let center = 0;
  let hasRenderedOnce = false;

  ringTrack.innerHTML = '';

  const cardEls = stories.map((story) => {
    const card = document.createElement('div');
    card.dataset.role = 'picker-card';
    card.className = 'absolute overflow-hidden cursor-pointer border-[0.5px] border-white/14 rounded-sm picker-card-el';
    card.style.left = '50%';
    card.style.top = '50%';
    card.style.width = '220px';
    card.style.height = '308px';
    card.style.background = '#0c0c0e';
    card.style.transformStyle = 'preserve-3d';
    card.style.willChange = 'transform, opacity, filter';
    card.style.transition = 'transform 0.78s cubic-bezier(0.16,1,0.3,1), opacity 0.55s ease, filter 0.55s ease, border-color 0.45s ease, box-shadow 0.55s ease';

    const img = document.createElement('img');
    img.src = story.coverImage;
    img.alt = story.title;
    img.className = 'w-full h-full object-cover';
    img.style.filter = 'grayscale(0.15) contrast(1.05)';
    card.appendChild(img);

    const shade = document.createElement('div');
    shade.className = 'absolute inset-0 pointer-events-none';
    shade.style.background = 'radial-gradient(120% 80% at 50% 100%, rgba(0,0,0,0.55) 0%, transparent 62%)';
    card.appendChild(shade);

    card.__story = story;
    return card;
  });
  cardEls.forEach((c) => ringTrack.appendChild(c));

  function getOffset(i, c) {
    let diff = i - c;
    const half = Math.floor(N / 2);
    diff = ((diff + N + half) % N) - half;
    if (N % 2 === 0 && diff === -half) diff = half;
    return diff;
  }

  function isReleased(story) {
    return story.status === 'released';
  }

  function applyCardVisualState(card, offset, story) {
    const released = isReleased(story);

    if (offset === 0) {
      card.style.zIndex = '5';
      card.style.transform = 'translate3d(-50%, -50%, 90px) scale(1.04)';
      if (released) {
        card.style.opacity = '1';
        card.style.filter = 'brightness(1) saturate(1)';
        card.style.borderColor = 'rgba(255,255,255,0.92)';
        card.style.boxShadow = '0 0 44px rgba(255,255,255,0.20), 0 18px 36px rgba(0,0,0,0.68)';
      } else {
        card.style.opacity = '0.55';
        card.style.filter = 'brightness(0.55) saturate(0.3)';
        card.style.borderColor = 'rgba(255,255,255,0.3)';
        card.style.boxShadow = '0 0 20px rgba(255,255,255,0.05), 0 18px 36px rgba(0,0,0,0.5)';
      }
    } else if (offset === -1) {
      card.style.zIndex = '2';
      card.style.transform = 'translate3d(calc(-50% - 300px), -50%, -60px) rotateY(32deg) scale(0.86)';
      card.style.opacity = released ? '0.62' : '0.4';
      card.style.filter = released ? 'brightness(0.72) saturate(0.6) blur(0.2px)' : 'brightness(0.42) saturate(0.2) blur(0.2px)';
      card.style.borderColor = 'rgba(255,255,255,0.18)';
      card.style.boxShadow = 'none';
    } else if (offset === 1) {
      card.style.zIndex = '2';
      card.style.transform = 'translate3d(calc(-50% + 300px), -50%, -60px) rotateY(-32deg) scale(0.86)';
      card.style.opacity = released ? '0.62' : '0.4';
      card.style.filter = released ? 'brightness(0.72) saturate(0.6) blur(0.2px)' : 'brightness(0.42) saturate(0.2) blur(0.2px)';
      card.style.borderColor = 'rgba(255,255,255,0.18)';
      card.style.boxShadow = 'none';
    } else {
      card.style.zIndex = '1';
      card.style.pointerEvents = 'none';
      const angle = offset * 32;
      const radius = 520;
      const x = Math.sin((angle * Math.PI) / 180) * radius;
      const z = Math.cos((angle * Math.PI) / 180) * radius - radius;
      card.style.transform = `translate3d(calc(-50% + ${x}px), -50%, ${z}px) rotateY(${-angle * 0.6}deg) scale(0.62)`;
      card.style.opacity = '0';
      card.style.filter = 'brightness(0.4) saturate(0) blur(2px)';
      card.style.borderColor = 'rgba(255,255,255,0.1)';
      card.style.boxShadow = 'none';
      return;
    }
    card.style.pointerEvents = 'auto';
  }

  function render() {
    cardEls.forEach((card, i) => {
      const offset = getOffset(i, center);
      applyCardVisualState(card, offset, card.__story);
    });

    const activeStory = stories[center];
    formSlot.innerHTML = '';

    function paintHeader() {
      titleEl.textContent = activeStory.title;
      hookEl.textContent = activeStory.hook || '';
      titleEl.style.opacity = '1';
      hookEl.style.opacity = '1';
    }

    if (!hasRenderedOnce) {
      paintHeader();
      hasRenderedOnce = true;
    } else {
      titleEl.style.opacity = '0';
      hookEl.style.opacity = '0';
      setTimeout(paintHeader, 150);
    }

    // READ button always visible/clickable now - click handler branches
    // on released status instead of hiding the button for locked stories
    enterBtn.style.opacity = '1';
    enterBtn.style.pointerEvents = 'auto';

    counterEl.textContent = `${center + 1} / ${N}`;
  }

  function go(dir) {
    center = (center + dir + N) % N;
    render();
  }

  cardEls.forEach((card, i) => {
    card.addEventListener('click', () => {
      const offset = getOffset(i, center);
      if (offset === 0) {
        const story = card.__story;
        if (isReleased(story)) {
          cachedOverlay.hidePicker();
          openStoryInReader(story);
        }
      } else {
        center = i;
        render();
      }
    });
  });

  ringTrack.querySelectorAll('[data-role="picker-card"]');

  const prevBtn = ringTrack.parentElement.querySelector('button[aria-label="Previous story"]');
  const nextBtn = ringTrack.parentElement.querySelector('button[aria-label="Next story"]');
  prevBtn.onclick = () => go(-1);
  nextBtn.onclick = () => go(1);

  enterBtn.onclick = () => {
    const story = stories[center];
    if (isReleased(story)) {
      cachedOverlay.hidePicker();
      openStoryInReader(story);
    } else {
      showEmailPopover({
        source: story.id,
        message: `"${story.title}" isn't published yet. Get notified when it drops.`,
      });
    }
  };

  let startX = 0;
  let isDown = false;
  ringTrack.onpointerdown = (e) => {
    isDown = true;
    startX = e.clientX;
    ringTrack.setPointerCapture(e.pointerId);
  };
  ringTrack.onpointerup = (e) => {
    if (!isDown) return;
    const dx = e.clientX - startX;
    if (dx < -60) go(1);
    if (dx > 60) go(-1);
    isDown = false;
  };

  render();
}

export function openStoryInReader(story) {
  const proseHTML = buildProseFromParagraphs(
    story.paragraphs,
    story.midPromptAfter,
    { headline: "Don't miss what's next.", copy: 'Get new myth fragments and early chapters \u2014 once a week, no spam.' }
  );
  // Story-specific reader background treatment. Currently only
  // "The One Who Stayed" needs a custom vignette/position/size - ported
  // faithfully from Meta's approved eye-bg mock (director: "execution
  // only, ignore the mock's suggestive comments/infra assumptions").
  // Scoped to this one story.id so no other reader is affected.
  const eyeTheme = story.id === 'the-one-who-stayed' ? {
    'cr-bg-position': 'center 42%',
    'cr-bg-size': '88% auto',
    'cr-overlay-1': 'radial-gradient(ellipse 70% 70% at 50% 42%, rgba(4,5,6,0.15) 0%, rgba(4,5,6,0.55) 42%, rgba(4,5,6,0.92) 72%, #040506 88%)',
  } : undefined;

  openChapterReader({
    publisher: "STYGIAN MYTH",
    workTitle: (story.collectionTitle || story.title).toUpperCase(),
    chapterLabel: story.seriesLabel || 'STORY',
    readTime: story.readTime || '4 MIN READ',
    title: story.title,
    proseHTML,
    isSubscribed: false,
    emailPromptOnceKey: `stygian_prompt_${story.id}`,
    theme: eyeTheme,
    themeClass: story.id === 'the-one-who-stayed' ? 'cr-theme--stayed' : undefined,
    readNext: [
      ...(() => {
        // Next story in the collection, if one exists - dynamic so
        // adding/reordering stories.js entries doesn't require
        // touching this file again.
        const idx = stories.findIndex((s) => s.id === story.id);
        const next = idx >= 0 ? stories[idx + 1] : null;
        if (!next) return [];
        const isLocked = next.status !== 'released';
        return [{
          title: `${next.seriesLabel ? next.seriesLabel + ' \u2014 ' : ''}${next.title}`,
          label: next.collectionTitle || 'CROW\u2019S TALES',
          ...(isLocked
            ? {}
            : { onClick: () => {
                // Recursive re-entry into openStoryInReader (unlike
                // Dawn's cross-link, which calls a separate function)
                // needs a tick of breathing room: closeChapterReaderInstant
                // triggers an async exitFullscreen() that must resolve
                // before the new reader's requestFullscreen() call, or
                // the browser silently drops the new fullscreen request.
                closeChapterReaderInstant();
                setTimeout(() => openStoryInReader(next), 60);
              } }),
        }];
      })(),
      {
        title: 'Dawn of the Fanatic \u2014 Day 0',
        label: 'MYTH\u2019S NOVELS \u2022 PROLOGUE',
        onClick: () => {
          closeChapterReaderInstant();
          openNovelPrologue('novel-01', [
            { title: 'Chapter I \u2014 The Pious, Before the Ash', label: 'NEXT CHAPTER \u2022 14 MIN' },
            {
              title: stories[0].title,
              label: 'STORY I \u2022 4 MIN READ',
              onClick: () => {
                closeChapterReaderInstant();
                openStoryPicker(stories);
              },
            },
          ]);
        },
      },
    ],
    backgroundUrl: story.readerImage || story.heroImage || story.coverImage,
  });
}

export function openStoryPicker(stories) {
  if (!cachedOverlay) {
    cachedOverlay = buildPicker();
  }
  cachedOverlay.showPicker(stories);
  return cachedOverlay.overlay;
}
